// Enhanced Base SceneElement class with Property Binding System
import { EnhancedConfigSchema, PropertyDefinition, SceneElementInterface } from '@core/types.js';
import { prop } from '@core/scene/plugins/plugin-sdk-prop-factories';
import { EmptyRenderObject, RenderObject } from '@core/render/render-objects';
import {
    PropertyBinding,
    ConstantBinding,
    MacroBinding,
    PropertyBindingUtils,
    PropertyBindingData,
    BindingType,
    type PropertyBindingContext,
} from '@bindings/property-bindings';
import { subscribeToMacroEvents, type MacroEvent } from '@state/scene/macroSyncService';
import { clearFeatureData } from '@audio/features/sceneApi';
import { getFeatureSubscriptionController } from '@audio/features/featureSubscriptionController';
import { getFeatureRequirements } from '../../../audio/audioElementMetadata';
import { debugLog } from '@utils/debug-log';
import { isTestEnvironment } from '@utils/env';
import { withRenderSafety, limitRenderObjects, DEFAULT_SAFETY_CONFIG } from '@core/scene/plugins/plugin-safety';
import { loadBundledAssetForElement } from '@core/scene/plugins/bundled-asset-registry';
import { BundledSprite, BundledSparrowHandle, BundledGridAtlasHandle } from '@core/resources/bundled-sprite';
import type { AtlasLayout } from '@core/resources/visual-source-descriptor';
import { VisualResourceHandle } from '@core/resources/visual-resource-handle';
import { useVisualAssetRegistryStore } from '@state/visualAssetRegistryStore';

// Lazy reference to avoid circular dependency with scene-element-registry
let _sceneElementRegistry: { getPluginId(type: string): string | undefined } | null = null;
function getSceneElementRegistry() {
    if (!_sceneElementRegistry) {
        // Dynamically resolve at first use (after all modules have initialized)
        import('@core/scene/registry/scene-element-registry').then((m) => {
            _sceneElementRegistry = m.sceneElementRegistry;
        });
    }
    return _sceneElementRegistry;
}

export type PropertyTransform<TValue, TElement = SceneElement> = (
    value: unknown,
    element: TElement
) => TValue | undefined;

export interface PropertyDescriptor<TValue = unknown, TElement = SceneElement> {
    defaultValue?: TValue;
    transform?: PropertyTransform<TValue, TElement>;
}

export type PropertyDescriptorMap<TElement = SceneElement> = Record<string, PropertyDescriptor<any, TElement>>;

type DescriptorValue<TDescriptor> = TDescriptor extends PropertyDescriptor<infer TValue, any> ? TValue : never;

export type PropertySnapshot<TDescriptors extends PropertyDescriptorMap<TElement>, TElement = SceneElement> = {
    [K in keyof TDescriptors]: DescriptorValue<TDescriptors[K]>;
};

export const asNumber: PropertyTransform<number, SceneElementInterface> = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === 'string') {
        const parsed = Number(value.trim());
        return Number.isFinite(parsed) ? parsed : undefined;
    }

    return undefined;
};

export const asBoolean: PropertyTransform<boolean, SceneElementInterface> = (value) => {
    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (normalized === 'true') {
            return true;
        }
        if (normalized === 'false') {
            return false;
        }
    }

    if (typeof value === 'number') {
        return value !== 0;
    }

    return undefined;
};

export const asString: PropertyTransform<string, SceneElementInterface> = (value) => {
    if (typeof value === 'string') {
        return value;
    }

    if (value == null) {
        return undefined;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }

    return undefined;
};

export const asTrimmedString: PropertyTransform<string, SceneElementInterface> = (value, element) => {
    const stringValue = asString(value, element);
    if (typeof stringValue !== 'string') {
        return undefined;
    }

    const trimmed = stringValue.trim();
    return trimmed.length > 0 ? trimmed : undefined;
};

const schemaRuntimeDescriptorCache: WeakMap<object, PropertyDescriptorMap<any>> = new WeakMap();

const _schemaIndex = new WeakMap<Function, Map<string, PropertyDefinition>>();

function getSchemaIndex(ctor: any): Map<string, PropertyDefinition> {
    if (!_schemaIndex.has(ctor)) {
        const map = new Map<string, PropertyDefinition>();
        const schema = ctor.getConfigSchema?.() as EnhancedConfigSchema | undefined;
        for (const tab of schema?.tabs ?? [])
            for (const group of tab.groups ?? [])
                for (const p of (group.properties ?? []) as PropertyDefinition[]) if (p?.key) map.set(p.key, p);
        _schemaIndex.set(ctor, map);
    }
    return _schemaIndex.get(ctor)!;
}

const hasOwn = (object: unknown, key: PropertyKey): boolean =>
    typeof object === 'object' && object !== null ? Object.prototype.hasOwnProperty.call(object, key) : false;

const degreesToRadians = (degrees: number): number => (degrees * Math.PI) / 180;
const radiansToDegrees = (radians: number): number => (radians * 180) / Math.PI;

export class SceneElement implements SceneElementInterface {
    public type: string;
    public id: string | null;

    // Property bindings - these replace direct property storage
    protected bindings: Map<string, PropertyBinding> = new Map();

    // Cache for frequently accessed values
    private _cachedValues: Map<string, any> = new Map();
    private _cacheValid: Map<string, boolean> = new Map();
    // Cache for computed scene element bounds (per target time bucket and mode)
    private _boundsCache: Map<string, { x: number; y: number; width: number; height: number }> = new Map();
    private _boundsDirty: boolean = true;

    private _macroUnsubscribe?: () => void;
    private _renderContext: PropertyBindingContext | null = null;
    private _keyframeBoundKeys: Set<string> = new Set();
    /** Handles/sprites registered via factory methods; destroyed automatically in dispose(). */
    private readonly _trackedVisualHandles: Array<{ destroy(): void }> = [];

    constructor(type: string, id: string | null = null, config: { [key: string]: any } = {}) {
        this.type = type;
        this.id = id;

        // Initialize default bindings from schema
        this._initializeDefaultBindings();

        // Apply configuration, converting values to bindings
        this._applyConfig(config);

        // Set up macro change listener to invalidate cache
        this._setupMacroListener();

        this._subscribeToRequiredFeatures();
    }

    /**
     * Set up listener for macro changes to invalidate cache
     */
    private _setupMacroListener(): void {
        this._macroUnsubscribe = subscribeToMacroEvents((event: MacroEvent) => {
            let requiresFeatureResubscribe = false;
            if (event.type === 'macroValueChanged') {
                this.bindings.forEach((binding, key) => {
                    if (binding instanceof MacroBinding && binding.getMacroId() === event.macroId) {
                        this._cacheValid.set(key, false);
                        this._invalidateBoundsCache();
                        if (this._isTrackRefProperty(key)) {
                            requiresFeatureResubscribe = true;
                        }
                    }
                });
            } else if (event.type === 'macroDeleted') {
                this.bindings.forEach((binding, key) => {
                    if (binding instanceof MacroBinding && binding.getMacroId() === event.macroId) {
                        const currentValue = binding.getValue();
                        this.bindings.set(key, new ConstantBinding(currentValue));
                        this._cacheValid.set(key, false);
                        this._invalidateBoundsCache();
                        if (this._isTrackRefProperty(key)) {
                            requiresFeatureResubscribe = true;
                        }
                    }
                });
            } else if (event.type === 'macrosImported') {
                // Imported snapshots may replace macro objects entirely; drop caches for macro-bound props.
                const trackRefKeys = this._findTrackRefKeys();
                this.bindings.forEach((binding, key) => {
                    if (binding instanceof MacroBinding) {
                        this._cacheValid.set(key, false);
                    }
                });
                this._invalidateBoundsCache();
                if (trackRefKeys.some((key) => this.bindings.get(key) instanceof MacroBinding)) {
                    requiresFeatureResubscribe = true;
                }
            }

            if (requiresFeatureResubscribe) {
                this._subscribeToRequiredFeatures();
            }
        });
    }

    protected onPropertyChanged(key: string, oldValue: unknown, newValue: unknown): void {
        if (oldValue !== newValue && this._isTrackRefProperty(key)) {
            this._subscribeToRequiredFeatures();
        }
    }

    private _isTrackRefProperty(key: string): boolean {
        const def = getSchemaIndex(this.constructor).get(key);
        return def ? def.type === 'timelineTrackRef' : key === 'audioTrackId';
    }

    private _findTrackRefKeys(): string[] {
        const keys: string[] = [];
        for (const [key, def] of getSchemaIndex(this.constructor)) {
            if (def.type === 'timelineTrackRef') {
                keys.push(key);
            }
        }
        return keys.length > 0 ? keys : ['audioTrackId'];
    }

    protected _subscribeToRequiredFeatures(): void {
        const requirements = getFeatureRequirements(this.type);
        const trackRefKeys = this._findTrackRefKeys();
        let rawTrack: string | null = null;
        for (const key of trackRefKeys) {
            if (this.bindings.get(key)) {
                const value = this.getProperty<string>(key);
                if (value) {
                    rawTrack = value;
                    break;
                }
            }
        }
        const controller = getFeatureSubscriptionController(this);
        controller.setStaticRequirements(requirements);
        controller.updateTrack(rawTrack);
    }

    /**
     * Lifecycle hook for subclasses to release resources.
     * The base implementation clears any lazily managed audio feature intents.
     */
    protected onDestroy(): void {
        clearFeatureData(this);
    }

    /**
     * Load a bundled asset from this plugin's assets/ directory.
     * Works in both dev mode (via the Vite dev server) and production (via the ZIP bundle).
     * Returns a URL string that can be used as an <img> src or similar.
     */
    protected loadBundledAsset(assetPath: string): Promise<string> {
        return loadBundledAssetForElement(this.type, assetPath);
    }

    /**
     * Create a managed handle for a bundled image asset.
     * Call `handle.get()` each frame to receive `{ resource, status }` ready for
     * `VisualMedia.setResource()`. Call `handle.destroy()` in `onDestroy()`.
     */
    protected bundledImage(filename: string): BundledSprite {
        const sprite = new BundledSprite(filename, (f) => this._makeBundledLoader(f));
        this._trackedVisualHandles.push(sprite);
        return sprite;
    }

    /**
     * Create a managed bundled-sprite helper for a plugin image asset.
     * Call `sprite.build(x, y, w, h)` each frame to get a ready `VisualMedia`
     * render object. Call `sprite.destroy()` in `onDestroy()`.
     */
    protected bundledSprite(filename: string): BundledSprite {
        const sprite = new BundledSprite(filename, (f) => this._makeBundledLoader(f));
        this._trackedVisualHandles.push(sprite);
        return sprite;
    }

    /**
     * Create a managed handle for a bundled Sparrow atlas (paired PNG + XML).
     * Call `handle.get()` each frame to receive `{ resource, status }` ready for
     * `VisualMedia.setResource()`. The atlas is automatically registered in the
     * visual asset registry so it appears in the Asset Manager.
     * Call `handle.destroy()` in `onDestroy()`.
     */
    protected bundledSparrow(pngFilename: string, xmlFilename: string, defaultFps?: number): BundledSparrowHandle {
        const handle = new BundledSparrowHandle(
            pngFilename,
            xmlFilename,
            (f) => this.loadBundledAsset(f),
            (pngUrl, xmlUrl) => {
                const registryId = `${this.type}:${pngFilename}`;
                const basename = pngFilename.split('/').pop() ?? pngFilename;
                const displayName = basename.replace(/\.[^.]+$/, '');
                useVisualAssetRegistryStore.getState().addPluginSparrowEntry(registryId, displayName, pngUrl, xmlUrl);
            },
            defaultFps
        );
        this._trackedVisualHandles.push(handle);
        return handle;
    }

    /**
     * Create a managed handle for a bundled grid-atlas spritesheet.
     *
     * The sheet is divided into a uniform grid described by `layout`. Call
     * `handle.get()` each frame to receive `{ resource, status }` ready for
     * `VisualMedia.setResource()`. Use `setAnimation(null)` and `setLocalTime(N)`
     * to freeze on a specific frame index N (with `frameDurationMs: 1000` in
     * the layout, localTimeSec N maps directly to frame N).
     *
     * The handle is automatically destroyed when the element is disposed.
     *
     * @example
     * private readonly _holds = this.bundledGridAtlas(
     *   'hold_assets.png',
     *   { columns: 4, rows: 2, frameDurationMs: 1000 }
     * );
     */
    protected bundledGridAtlas(filename: string, layout: AtlasLayout): BundledGridAtlasHandle {
        const handle = new BundledGridAtlasHandle(filename, layout, (f) => this.loadBundledAsset(f));
        this._trackedVisualHandles.push(handle);
        return handle;
    }

    /**
     * Create a `VisualResourceHandle` that is automatically destroyed when this
     * element is disposed. Use instead of `new VisualResourceHandle()` so you
     * don't need a manual `handle.destroy()` call in `onDestroy()`.
     *
     * @example
     * private readonly _handle = this.visualHandle();
     */
    protected visualHandle(): VisualResourceHandle {
        const handle = new VisualResourceHandle();
        this._trackedVisualHandles.push(handle);
        return handle;
    }

    private async _makeBundledLoader(filename: string): Promise<string> {
        const url = await this.loadBundledAsset(filename);
        const registryId = `${this.type}:${filename}`;
        const basename = filename.split('/').pop() ?? filename;
        const displayName = basename.replace(/\.[^.]+$/, '');
        const type = /\.gif$/i.test(filename) ? ('gif' as const) : ('image' as const);
        useVisualAssetRegistryStore.getState().addPluginEntry(registryId, displayName, url, type);
        return url;
    }

    /**
     * Dispose element resources and detach listeners
     */
    dispose(): void {
        this.onDestroy();
        const trackedVisualHandles = Array.isArray((this as any)._trackedVisualHandles)
            ? (this as any)._trackedVisualHandles
            : [];
        for (const h of trackedVisualHandles) h.destroy();
        if (this._macroUnsubscribe) {
            this._macroUnsubscribe();
            this._macroUnsubscribe = undefined;
        }
    }

    /**
     * Initialize default bindings from the element's config schema
     */
    private _initializeDefaultBindings(): void {
        const schema: EnhancedConfigSchema = (this.constructor as any).getConfigSchema();
        if (schema && schema.tabs) {
            for (const group of schema.tabs.flatMap((t) => t.groups)) {
                for (const prop of group.properties as PropertyDefinition[]) {
                    if (prop && prop.key && prop.default !== undefined && prop.key !== 'id' && prop.key !== 'type') {
                        this.bindings.set(prop.key, new ConstantBinding(prop.default));
                    }
                }
            }
        }

        // Set default bindings for base properties
        this.bindings.set('visible', new ConstantBinding(true));
        this.bindings.set('zIndex', new ConstantBinding(0));
        this.bindings.set('offsetX', new ConstantBinding(0));
        this.bindings.set('offsetY', new ConstantBinding(0));
        this.bindings.set('elementScaleX', new ConstantBinding(1));
        this.bindings.set('elementScaleY', new ConstantBinding(1));
        this.bindings.set('elementRotation', new ConstantBinding(0));
        this.bindings.set('elementSkewX', new ConstantBinding(0));
        this.bindings.set('elementSkewY', new ConstantBinding(0));
        this.bindings.set('anchorX', new ConstantBinding(0.5));
        this.bindings.set('anchorY', new ConstantBinding(0.5));
        this.bindings.set('elementOpacity', new ConstantBinding(1));
    }

    /**
     * Get a property value through its binding
     */
    protected getProperty<T>(key: string): T {
        // Check cache first
        if (this._cacheValid.get(key)) {
            return this._cachedValues.get(key);
        }

        const binding = this.bindings.get(key);
        if (!binding) {
            if (!isTestEnvironment()) {
                console.warn(`No binding found for property '${key}' in element ${this.id}`);
            }
            return undefined as T;
        }

        const context = this._renderContext;
        let value =
            context && typeof binding.getValueWithContext === 'function'
                ? binding.getValueWithContext(context)
                : binding.getValue();

        // Cache the value
        this._cachedValues.set(key, value);
        this._cacheValid.set(key, true);
        // Property read doesn't affect bounds cache

        return value;
    }

    protected getProps<TDescriptors extends PropertyDescriptorMap<this>>(
        descriptors: TDescriptors
    ): PropertySnapshot<TDescriptors, this> {
        const resolved = {} as PropertySnapshot<TDescriptors, this>;

        for (const key of Object.keys(descriptors) as Array<keyof TDescriptors>) {
            const descriptor = descriptors[key];
            const raw = this.getProperty(key as string);
            const transform = descriptor?.transform as PropertyTransform<unknown, this> | undefined;
            const transformed = transform ? transform(raw, this) : raw;
            const finalValue =
                transformed !== undefined && transformed !== null ? transformed : descriptor?.defaultValue;

            resolved[key] = finalValue as PropertySnapshot<TDescriptors, this>[typeof key];
        }

        return resolved;
    }

    protected getSchemaProps<TAdditional extends PropertyDescriptorMap<this>>(
        additionalDescriptors?: TAdditional
    ): PropertySnapshot<PropertyDescriptorMap<this> & TAdditional, this> {
        const ctor = this.constructor as typeof SceneElement;
        let baseDescriptors = schemaRuntimeDescriptorCache.get(ctor);
        if (!baseDescriptors) {
            baseDescriptors = SceneElement._collectSchemaRuntimeDescriptors(ctor);
            schemaRuntimeDescriptorCache.set(ctor, baseDescriptors);
        }

        const typedBase = baseDescriptors as PropertyDescriptorMap<this>;
        if (!additionalDescriptors || Object.keys(additionalDescriptors).length === 0) {
            const baseOnly = this.getProps(typedBase as PropertyDescriptorMap<this>);
            return baseOnly as PropertySnapshot<PropertyDescriptorMap<this> & TAdditional, this>;
        }

        const merged = {
            ...(typedBase as Record<string, PropertyDescriptor<any, this>>),
            ...(additionalDescriptors as Record<string, PropertyDescriptor<any, this>>),
        } as PropertyDescriptorMap<this> & TAdditional;

        return this.getProps(merged);
    }

    private static _collectSchemaRuntimeDescriptors(ctor: typeof SceneElement): PropertyDescriptorMap<any> {
        const descriptors: PropertyDescriptorMap<any> = {};
        try {
            const schema = typeof ctor.getConfigSchema === 'function' ? ctor.getConfigSchema() : null;
            const groups = schema?.tabs.flatMap((t) => t.groups) ?? [];
            for (const group of groups) {
                if (!group?.properties) continue;
                for (const property of group.properties as PropertyDefinition[]) {
                    if (!property) continue;
                    const runtime = property.runtime;
                    if (!runtime) continue;
                    const key = runtime.runtimeKey ?? property.key;
                    if (!key) continue;

                    const descriptor: PropertyDescriptor<any, SceneElement> = {};
                    const runtimeConfig = runtime as NonNullable<PropertyDefinition['runtime']>;
                    if (typeof runtimeConfig.transform === 'function') {
                        descriptor.transform = runtimeConfig.transform as PropertyTransform<any, SceneElement>;
                    }

                    if (hasOwn(runtimeConfig, 'defaultValue')) {
                        descriptor.defaultValue = runtimeConfig.defaultValue;
                    } else if (hasOwn(property, 'default')) {
                        descriptor.defaultValue = property.default;
                    }

                    descriptors[key] = descriptor;
                }
            }
        } catch (error) {
            debugLog('Failed to collect schema runtime descriptors', error);
        }

        return descriptors;
    }

    /**
     * Set a property value through its binding
     */
    protected setProperty<T>(key: string, value: T): void {
        let oldValue: unknown = undefined;
        const existingBinding = this.bindings.get(key);
        if (existingBinding) {
            try {
                oldValue = this.getProperty(key);
            } catch (error) {
                oldValue = undefined;
            }
        }

        if (!existingBinding) {
            // Create a new constant binding
            this.bindings.set(key, new ConstantBinding(value));
        } else {
            existingBinding.setValue(value);
        }

        // Invalidate cache
        this._cacheValid.set(key, false);
        this._invalidateBoundsCache();

        let newValue: unknown = undefined;
        const updatedBinding = this.bindings.get(key);
        if (updatedBinding) {
            try {
                newValue = this.getProperty(key);
            } catch (error) {
                newValue = undefined;
            }
        }

        if (oldValue !== newValue) {
            this.onPropertyChanged(key, oldValue, newValue);
        }
    }

    /**
     * Bind a property to a macro
     */
    bindToMacro(propertyKey: string, macroId: string): void {
        this.bindings.set(propertyKey, new MacroBinding(macroId));
        this._cacheValid.set(propertyKey, false);
        this._invalidateBoundsCache();
    }

    /**
     * Convert a property to a constant binding
     */
    unbindFromMacro(propertyKey: string): void {
        const binding = this.bindings.get(propertyKey);
        if (binding instanceof MacroBinding) {
            // Get the current value and make it constant
            const currentValue = binding.getValue();
            this.bindings.set(propertyKey, new ConstantBinding(currentValue));
            this._cacheValid.set(propertyKey, false);
            this._invalidateBoundsCache();
        }
    }

    /**
     * Get property binding for inspection
     */
    getBinding(propertyKey: string): PropertyBinding | undefined {
        return this.bindings.get(propertyKey);
    }

    /**
     * Set property binding directly
     */
    setBinding(propertyKey: string, binding: PropertyBinding): void {
        this.bindings.set(propertyKey, binding);
        this._cacheValid.set(propertyKey, false);
        this._invalidateBoundsCache();
    }

    /**
     * Check if a property is bound to a specific macro
     */
    isBoundToMacro(propertyKey: string, macroId: string): boolean {
        const binding = this.bindings.get(propertyKey);
        return PropertyBindingUtils.isBoundToMacro(binding, macroId);
    }

    /**
     * Get all properties that are bound to macros
     */
    getMacroBoundProperties(): { [propertyKey: string]: string } {
        const result: { [propertyKey: string]: string } = {};
        this.bindings.forEach((binding, key) => {
            if (binding instanceof MacroBinding) {
                result[key] = binding.getMacroId();
            }
        });
        return result;
    }

    /**
     * Search through bindings for bindings of a certain type
     * @param bindingType - The type of binding to search for ('macro' | 'constant')
     * @returns Array of objects containing property path and binding details
     */
    getBindingsByType(bindingType: BindingType): Array<{ propertyPath: string; binding: PropertyBinding }> {
        const result: Array<{ propertyPath: string; binding: PropertyBinding }> = [];
        this.bindings.forEach((binding, propertyPath) => {
            if (binding.type === bindingType) {
                result.push({ propertyPath, binding });
            }
        });
        return result;
    }

    /**
     * Get all macro bindings for a specific macro
     * @param macroId - The ID of the macro to search for
     * @returns Array of property paths that are bound to this macro
     */
    getMacroBindingsForMacro(macroId: string): string[] {
        const result: string[] = [];
        this.bindings.forEach((binding, propertyPath) => {
            if (binding instanceof MacroBinding && binding.getMacroId() === macroId) {
                result.push(propertyPath);
            }
        });
        return result;
    }

    // Property getters using the binding system
    get visible(): boolean {
        return this.getProperty('visible');
    }
    get zIndex(): number {
        return this.getProperty('zIndex');
    }
    get offsetX(): number {
        return this.getProperty('offsetX');
    }
    get offsetY(): number {
        return this.getProperty('offsetY');
    }
    get elementScaleX(): number {
        return this.getProperty('elementScaleX');
    }
    get elementScaleY(): number {
        return this.getProperty('elementScaleY');
    }
    get elementRotation(): number {
        return this.getProperty('elementRotation');
    }
    get elementSkewX(): number {
        return this.getProperty('elementSkewX');
    }
    get elementSkewY(): number {
        return this.getProperty('elementSkewY');
    }
    get anchorX(): number {
        return this.getProperty('anchorX');
    }
    get anchorY(): number {
        return this.getProperty('anchorY');
    }
    get elementOpacity(): number {
        return this.getProperty('elementOpacity');
    }

    /**
     * Template method for building RenderObjects with automatic transform application
     * Child classes should override _buildRenderObjects instead
     */
    buildRenderObjects(config: any, targetTime: number): RenderObject[] {
        // Set render context and invalidate keyframe caches BEFORE any property reads
        // so that time-dependent bindings evaluate at the correct targetTime.
        this._renderContext = { targetTime, sceneConfig: config };

        // Invalidate cache for keyframe-bound properties each frame (values are time-dependent)
        for (const key of this._keyframeBoundKeys) {
            this._cacheValid.set(key, false);
        }

        if (!this.visible) {
            this._renderContext = null;
            return [];
        }

        // Apply safety controls for plugin elements (lazy import to avoid circular dependency)
        const pluginId = getSceneElementRegistry()?.getPluginId(this.type);

        let childRenderObjects: RenderObject[];

        if (pluginId) {
            // This is a plugin element - apply safety controls
            const result = withRenderSafety(() => this._buildRenderObjects(config, targetTime), DEFAULT_SAFETY_CONFIG, {
                pluginId,
                elementType: this.type,
            });

            if (result === null) {
                // Render failed or timed out
                this._renderContext = null;
                return [];
            }

            // Limit render object count
            childRenderObjects = limitRenderObjects(result, DEFAULT_SAFETY_CONFIG, {
                pluginId,
                elementType: this.type,
            });
        } else {
            // Built-in element - no safety wrapper needed
            childRenderObjects = this._buildRenderObjects(config, targetTime);
        }

        if (childRenderObjects.length === 0) {
            this._renderContext = null;
            return [];
        }

        // Calculate the layout and visual bounding boxes and anchor point for transformation
        const layoutBounds = this._getCachedSceneElementBounds(childRenderObjects, targetTime, 'layout');
        const visualBounds = this._getCachedSceneElementBounds(childRenderObjects, targetTime, 'visual');

        // Create an empty render object that will contain all child objects.
        // setOriginFraction stores the anchor fractions lazily; EmptyRenderObject resolves
        // them to pixel originX/Y from baseBounds at render/bounds time.
        const containerObject = new EmptyRenderObject(
            this.offsetX,
            this.offsetY,
            this.elementScaleX,
            this.elementScaleY,
            this.elementOpacity
        );
        const elementRotationRadians = degreesToRadians(this.elementRotation);

        containerObject
            .setRotation(elementRotationRadians)
            .setSkew(this.elementSkewX, this.elementSkewY)
            .setVisible(this.visible)
            .setOriginFraction(this.anchorX, this.anchorY);

        // Add all child render objects to the container
        for (const childObj of childRenderObjects) {
            if (childObj) {
                // Cast to any to accommodate migrated TS RenderObject classes vs previous interface
                (containerObject as any).addChild(childObj as any);
            }
        }

        // Store the untransformed aggregate bounds for later transform math (selection, handles).
        // baseBounds must be set before render/bounds queries so _resolveOriginFractions works.
        (containerObject as any).baseBounds = { ...layoutBounds };
        (containerObject as any).elementTransform = {
            offsetX: this.offsetX,
            offsetY: this.offsetY,
            scaleX: this.elementScaleX,
            scaleY: this.elementScaleY,
            rotation: elementRotationRadians,
            skewX: this.elementSkewX,
            skewY: this.elementSkewY,
        };

        // Clear render context after all property reads are complete
        this._renderContext = null;

        // Add anchor point visualization if enabled
        if (config.showAnchorPoints) {
            containerObject.setAnchorVisualizationData(layoutBounds, visualBounds, this.anchorX, this.anchorY);
        }

        return [containerObject];
    }

    /**
     * Abstract method for child classes to implement their specific rendering logic
     */
    protected _buildRenderObjects(config: any, targetTime: number): RenderObject[] {
        // Default implementation returns empty array
        return [];
    }

    /**
     * Calculate the bounding box of all child render objects
     */
    private _calculateSceneElementBounds(
        renderObjects: RenderObject[],
        mode: 'visual' | 'layout' = 'layout'
    ): {
        x: number;
        y: number;
        width: number;
        height: number;
    } {
        if (renderObjects.length === 0) {
            return { x: 0, y: 0, width: 0, height: 0 };
        }

        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;
        let validBoundsCount = 0;

        for (const obj of renderObjects) {
            if (!obj) continue;
            let bounds: any = null;
            if (mode === 'visual' && (obj as any).getVisualBounds) bounds = (obj as any).getVisualBounds();
            else if (mode === 'layout' && (obj as any).getLayoutBounds) bounds = (obj as any).getLayoutBounds();

            if (!bounds) continue; // layout may return null when excluded
            if (this._validateBounds(bounds, obj)) {
                minX = Math.min(minX, bounds.x);
                minY = Math.min(minY, bounds.y);
                maxX = Math.max(maxX, bounds.x + bounds.width);
                maxY = Math.max(maxY, bounds.y + bounds.height);
                validBoundsCount++;
            }
        }

        if (validBoundsCount === 0) {
            console.warn(`No valid bounds found for scene element ${this.id}, returning empty bounds`);
            return { x: 0, y: 0, width: 0, height: 0 };
        }

        return {
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY,
        };
    }

    /**
     * Retrieve scene element bounds from cache or compute and cache them.
     * Uses a time bucket (ms) to avoid excessive keys; invalidated on any property/macro change.
     */
    private _getCachedSceneElementBounds(
        renderObjects: RenderObject[],
        targetTime: number,
        mode: 'visual' | 'layout' = 'layout'
    ): { x: number; y: number; width: number; height: number } {
        const timeBucket = Math.floor((isFinite(targetTime) ? targetTime : 0) * 1000);
        const cacheKey = `${mode}:${timeBucket}`;
        if (!this._boundsDirty && this._boundsCache.has(cacheKey)) {
            const cached = this._boundsCache.get(cacheKey)!;
            return { ...cached };
        }

        const computed = this._calculateSceneElementBounds(renderObjects, mode);

        // Update cache and mark clean for this mode/time
        this._boundsCache.set(cacheKey, computed);
        this._boundsDirty = false;

        // Prune cache to a small size to prevent growth (per mode/time entries)
        const MAX_ENTRIES = 16;
        if (this._boundsCache.size > MAX_ENTRIES) {
            const keys = Array.from(this._boundsCache.keys()).sort();
            while (this._boundsCache.size > MAX_ENTRIES) {
                const k = keys.shift();
                if (typeof k !== 'undefined') this._boundsCache.delete(k);
            }
        }

        return computed;
    }

    /**
     * Validate bounds object for correctness
     */
    private _validateBounds(bounds: any, obj?: any): boolean {
        if (!bounds || typeof bounds !== 'object') {
            return false;
        }

        const { x, y, width, height } = bounds;

        if (typeof x !== 'number' || typeof y !== 'number' || typeof width !== 'number' || typeof height !== 'number') {
            return false;
        }

        if (!isFinite(x) || !isFinite(y) || !isFinite(width) || !isFinite(height)) {
            return false;
        }

        if (width < 0 || height < 0) {
            console.warn(`Negative dimensions detected in bounds:`, bounds, obj?.constructor?.name);
            return false;
        }

        return true;
    }

    /**
     * Get the configuration schema for this element type
     */
    static getConfigSchema(): EnhancedConfigSchema {
        return {
            name: 'Bound Base Element',
            description: 'Base scene element with property bindings',
            category: 'general',
            tabs: [
                {
                    id: 'element',
                    label: 'Element',
                    groups: [
                        {
                            id: 'basicVisibility',
                            label: 'Visibility & Layer',
                            collapsed: false,
                            description: 'Control whether the element is visible and how it blends with other layers.',
                            properties: [
                                prop.boolean('visible', 'Visible', true),
                                prop.number('elementOpacity', 'Opacity (0–1)', 1, {
                                    min: 0,
                                    max: 1,
                                    step: 0.01,
                                    description: 'Element transparency (0 = transparent, 1 = opaque).',
                                }),
                                prop.number('zIndex', 'Layer Order', 0, {
                                    min: 0,
                                    max: 100,
                                    step: 1,
                                    description: 'Stacking order for overlapping layers (higher values appear on top).',
                                }),
                            ],
                        },
                        {
                            id: 'basicTransform',
                            label: 'Position, Rotation & Scale',
                            collapsed: false,
                            description:
                                'Set the element position and size adjustments relative to its default layout.',
                            properties: [
                                prop.number('offsetX', 'Offset X (px)', 0, {
                                    step: 1,
                                    description: 'Horizontal position offset in pixels.',
                                }),
                                prop.number('offsetY', 'Offset Y (px)', 0, {
                                    step: 1,
                                    description: 'Vertical position offset in pixels.',
                                }),
                                prop.number('elementRotation', 'Rotation (°)', 0, {
                                    step: 1,
                                    description: 'Element rotation in degrees.',
                                }),
                                prop.number('elementScaleX', 'Scale X (multiplier)', 1, {
                                    step: 0.01,
                                    description: 'Horizontal scaling factor.',
                                }),
                                prop.number('elementScaleY', 'Scale Y (multiplier)', 1, {
                                    step: 0.01,
                                    description: 'Vertical scaling factor.',
                                }),
                            ],
                        },
                        {
                            id: 'advancedAnchor',
                            label: 'Anchor & Skew',
                            collapsed: true,
                            description: 'Advanced pivot, rotation, and skew controls.',
                            properties: [
                                prop.number('anchorX', 'Anchor X (0–1)', 0.5, {
                                    min: 0,
                                    max: 1,
                                    step: 0.01,
                                    description: 'Horizontal anchor point (0 = left, 1 = right).',
                                }),
                                prop.number('anchorY', 'Anchor Y (0–1)', 0.5, {
                                    min: 0,
                                    max: 1,
                                    step: 0.01,
                                    description: 'Vertical anchor point (0 = top, 1 = bottom).',
                                }),
                                prop.number('elementSkewX', 'Skew X', 0, {
                                    step: 0.01,
                                    description: 'Horizontal skew in radians.',
                                }),
                                prop.number('elementSkewY', 'Skew Y', 0, {
                                    step: 0.01,
                                    description: 'Vertical skew in radians.',
                                }),
                            ],
                        },
                    ],
                },
            ],
        };
    }

    /**
     * Get current configuration including binding information
     */
    getConfig(): { [key: string]: any } {
        const config: { [key: string]: any } = {
            id: this.id,
            type: this.type,
        };

        // Add all bound properties with their current values
        this.bindings.forEach((binding, key) => {
            const val: any = binding.getValue();
            config[key] = val;
        });

        return config;
    }

    /**
     * Get configuration with binding metadata for serialization
     */
    getSerializableConfig(): { [key: string]: any } {
        const config: { [key: string]: any } = {
            id: this.id,
            type: this.type,
        };

        // Add all bindings in serialized form (debug logging only when enabled)
        this.bindings.forEach((binding, key) => {
            debugLog('[Bindings][Serialize]', key, binding);
            config[key] = binding.serialize();
        });

        return config;
    }

    /**
     * Apply configuration from either raw values or binding data
     */
    protected _applyConfig(config: { [key: string]: any }): void {
        for (const [key, value] of Object.entries(config)) {
            if (key === 'id' || key === 'type') continue;

            let oldValue: unknown = undefined;
            const hadBinding = this.bindings.has(key);
            if (hadBinding) {
                try {
                    oldValue = this.getProperty(key);
                } catch (error) {
                    oldValue = undefined;
                }
            }

            // Check if this is binding data
            if (
                value &&
                typeof value === 'object' &&
                value.type &&
                (value.type === 'constant' || value.type === 'macro' || value.type === 'keyframes')
            ) {
                // This is serialized binding data
                const binding = PropertyBinding.fromSerialized(value as PropertyBindingData);
                this.bindings.set(key, binding);
                // Track keyframe-bound keys for per-frame cache invalidation
                if (value.type === 'keyframes') {
                    this._keyframeBoundKeys.add(key);
                } else {
                    this._keyframeBoundKeys.delete(key);
                }
            } else {
                // Raw value OR already-instantiated binding — not keyframes
                this._keyframeBoundKeys.delete(key);
                if (value instanceof PropertyBinding) {
                    this.bindings.set(key, value);
                } else {
                    // Unwrap any accidental nested constant binding object shapes
                    const unwrap = (v: any, depth = 0): any => {
                        if (depth > 10) return v; // safety guard
                        if (v && typeof v === 'object' && v.type === 'constant' && 'value' in v) {
                            return unwrap(v.value, depth + 1);
                        }
                        return v;
                    };
                    const raw = unwrap(value);
                    this.bindings.set(key, new ConstantBinding(raw));
                }
            }

            this._cacheValid.set(key, false);
            this._invalidateBoundsCache();

            let newValue: unknown = undefined;
            const binding = this.bindings.get(key);
            if (binding) {
                try {
                    newValue = this.getProperty(key);
                } catch (error) {
                    newValue = undefined;
                }
            }

            if (oldValue !== newValue) {
                this.onPropertyChanged(key, oldValue, newValue);
            }
        }
    }

    /**
     * Update configuration with new values
     */
    updateConfig(newConfig: { [key: string]: any }): this {
        this._applyConfig(newConfig);
        return this;
    }

    /** Invalidate the scene element bounds cache */
    protected _invalidateBoundsCache(): void {
        this._boundsDirty = true;
        this._boundsCache.clear();
    }

    /**
     * Exposed hook so external lifecycle events (e.g., async font loads) can force a bounds refresh.
     */
    public markBoundsDirty(): void {
        this._invalidateBoundsCache();
    }

    // Setter methods that work with the binding system
    setVisible(visible: boolean): this {
        this.setProperty('visible', visible);
        return this;
    }

    setZIndex(zIndex: number): this {
        this.setProperty('zIndex', zIndex);
        return this;
    }

    setOffsetX(offsetX: number): this {
        this.setProperty('offsetX', offsetX);
        return this;
    }

    setOffsetY(offsetY: number): this {
        this.setProperty('offsetY', offsetY);
        return this;
    }

    setOffset(offsetX: number, offsetY: number): this {
        this.setProperty('offsetX', offsetX);
        this.setProperty('offsetY', offsetY);
        return this;
    }

    setElementScaleX(scaleX: number): this {
        this.setProperty('elementScaleX', scaleX);
        return this;
    }

    setElementScaleY(scaleY: number): this {
        this.setProperty('elementScaleY', scaleY);
        return this;
    }

    setElementScale(scaleX: number, scaleY: number = scaleX): this {
        this.setProperty('elementScaleX', scaleX);
        this.setProperty('elementScaleY', scaleY);
        return this;
    }

    setElementRotation(rotation: number): this {
        this.setProperty('elementRotation', rotation);
        return this;
    }

    setElementRotationRadians(rotation: number): this {
        this.setProperty('elementRotation', radiansToDegrees(rotation));
        return this;
    }

    setAnchorX(anchorX: number): this {
        this.setProperty('anchorX', Math.max(0, Math.min(1, anchorX)));
        return this;
    }

    setAnchorY(anchorY: number): this {
        this.setProperty('anchorY', Math.max(0, Math.min(1, anchorY)));
        return this;
    }

    setAnchor(anchorX: number, anchorY: number): this {
        this.setAnchorX(anchorX);
        this.setAnchorY(anchorY);
        return this;
    }

    setElementSkewX(skewX: number): this {
        this.setProperty('elementSkewX', skewX);
        return this;
    }

    setElementSkewY(skewY: number): this {
        this.setProperty('elementSkewY', skewY);
        return this;
    }

    setElementSkew(skewX: number, skewY: number): this {
        this.setElementSkewX(skewX);
        this.setElementSkewY(skewY);
        return this;
    }

    setElementOpacity(opacity: number): this {
        this.setProperty('elementOpacity', Math.max(0, Math.min(1, opacity)));
        return this;
    }
}
